import { $, GraphQLTypes, InputType, Selector } from '@zeus/index';
import { typedGql } from '@zeus/typedDocumentNode';
import { scalars } from '~/gql/utils/scalars';

const scoreSelector = Selector('BattleScore')({
  crownCount: true,
  badges: true,
  turn: true,
});

const finishedBattleSelector = Selector('FinishedBattle')({
  date: true,
  isWinner: true,
  opponent: {
    avatar: {
      fileName: true,
      id: true,
    },
    username: true,
  },
  isChallenger: true,
});

const pendingBattleSelector = Selector('PendingBattle')({
  id: true,
  challenger: {
    battleStatus: true,
    username: true,
    avatar: {
      fileName: true,
    },
    id: true,
    wonBattles: true,
  },
  createdAt: true,
  updatedAt: true,
  ttl: true,
  currentPlayerId: true,
});

const challengeableUsersSelector = Selector('ChallengeableUser')({
  id: true,
  avatar: {
    fileName: true,
    id: true,
  },
  username: true,
  wonBattles: true,
  battleStatus: true,
});

const sentBattleSelector = Selector('SentBattles')({
  id: true,
  avatar: {
    fileName: true,
    id: true,
  },
  username: true,
  wonBattles: true,
  battleStatus: true,
  createdAt: true,
});

const availableChallengersSelector = Selector('AvailableChallengers')({
  challengeableFriends: challengeableUsersSelector,
  challengeableUsers: challengeableUsersSelector,
});

export const GQL_GET_AVAILABLE_CHALLENGERS = typedGql('query', { scalars })({
  getAvailableChallengers: [
    {
      searchText: $('searchText', 'String'),
    },
    availableChallengersSelector,
  ],
});

export const GQL_GET_SENT_BATTLES = typedGql('query', { scalars })({
  getSentBattles: sentBattleSelector,
});

const battleTrackingSelector = Selector('BattleTracking')({
  id: true,
  battlePlayerTrackingId: true,
  currentPlayerId: true,
  challenger: {
    username: true,
    score: scoreSelector,
    id: true,
    avatar: {
      fileName: true,
      id: true,
    },
  },
  challenged: {
    username: true,
    score: scoreSelector,
    id: true,
    avatar: {
      fileName: true,
      id: true,
    },
  },
});

const acceptedChallengeSelector = Selector('BattleTracking')({
  ...battleTrackingSelector,
});

export const GQL_CREATE_BATTLE = typedGql('mutation', { scalars })({
  createBattle: [
    { challengedId: $('challengedId', 'String!') },
    battleTrackingSelector,
  ],
});

export const GQL_GET_BATTLE_QUESTION = typedGql('query', { scalars })({
  getBattleQuestion: [
    {
      themeId: $('themeId', 'String!'),
      battlePlayerTrackingId: $('battlePlayerTrackingId', 'String!'),
    },
    {
      id: true,
      text: true,
      answers: {
        id: true,
        text: true,
      },
      picture: {
        id: true,
        fileName: true,
      },
      theme: {
        id: true,
        name: true,
      },
    },
  ],
});

export const GQL_VALIDATE_BATTLE_ANSWER = typedGql('mutation', { scalars })({
  validateBattleAnswer: [
    {
      input: {
        answerId: $('answerId', 'String!'),
        battlePlayerTrackingId: $('battlePlayerTrackingId', 'String!'),
        isBonusCrown: $('isBonusCrown', 'Boolean!'),
        isChallenger: $('isChallenger', 'Boolean!'),
        questionId: $('questionId', 'String!'),
        themeId: $('themeId', 'String!'),
      },
    },
    {
      badges: true,
      currentTurn: true,
      crownCount: true,
      correctAnswerId: true,
      turnEnded: true,
    },
  ],
});

export const GQL_GET_PENDING_BATTLES = typedGql('query', { scalars })({
  getPendingBattles: pendingBattleSelector,
});

export const GQL_ACCEPT_CHALLENGE = typedGql('mutation', { scalars })({
  acceptChallenge: [
    {
      battleId: $('battleId', 'String!'),
    },
    acceptedChallengeSelector,
  ],
});

export const GQL_FINISHED_BATTLES = typedGql('query', { scalars })({
  getFinishedBattles: {
    ...finishedBattleSelector,
  },
});

export const GQL_DECLINE_CHALLENGE = typedGql('mutation', { scalars })({
  declineChallenge: [
    {
      battleId: $('battleId', 'String!'),
    },
    true,
  ],
});

export const GQL_QUIT_BATTLE = typedGql('mutation', { scalars })({
  quitBattle: [
    {
      battlePlayerTrackingId: $('battlePlayerTrackingId', 'String!'),
    },
    true,
  ],
});

export const GQL_AWARD_BONUS_CROWN = typedGql('mutation', { scalars })({
  awardBonusCrown: [
    {
      battlePlayerTrackingId: $('battlePlayerTrackingId', 'String!'),
    },
    {
      crownCount: true,
    },
  ],
});

export const GQL_GET_BATTLE_TRACKING = typedGql('query', { scalars })({
  getBattlePlayerTrackingById: [
    {
      battlePlayerTrackingId: $('battlePlayerTrackingId', 'String!'),
    },
    acceptedChallengeSelector,
  ],
});

export const GQL_GET_FINISHED_BATTLES = typedGql('query', { scalars })({
  getFinishedBattles: {
    opponent: {
      avatar: {
        id: true,
        fileName: true,
      },
    },
    isWinner: true,
    isChallenger: true,
    data: true,
  },
});

export const GQL_GET_WINNER = typedGql('query', { scalars })({
  getWinner: [
    {
      battleTrackingId: $('battleTrackingId', 'String!'),
    },
    {
      isWinner: true,
      opponent: true,
    },
  ],
});

export const GQL_CHECK_BATTLES = typedGql('query', { scalars })({
  checkBattles: {
    hasFinishedBattles: true,
    hasPendingBattles: true,
  },
});

export type StoredBattleTracking = InputType<
  GraphQLTypes['BattleTracking'],
  typeof acceptedChallengeSelector
>;

export type BattleStatus = GraphQLTypes['BattleStatus'];

export type StoredChallengeableUser = InputType<
  GraphQLTypes['ChallengeableUser'],
  typeof challengeableUsersSelector
>;

export type StoredPendingBattle = InputType<
  GraphQLTypes['PendingBattle'],
  typeof pendingBattleSelector
>;

export type StoredFinishedBattle = InputType<
  GraphQLTypes['FinishedBattle'],
  typeof finishedBattleSelector
>;

export type StoredsentBattleSelector = InputType<
  GraphQLTypes['SentBattles'],
  typeof sentBattleSelector
>;
